import {
  requestOpenapiHydrationCacheAdd,
  requestOpenapiHydrationCacheClear,
  requestOpenapiHydrationCacheHas,
  requestOpenapiHydrationCacheRemove,
  sendOpenapiHydrationCacheResponse,
} from '../actions'
import { sendMessage } from '@wellesley/host-remote/common/message/sendMessage'
import { listenMessage } from '@wellesley/host-remote/common/message/listenMessage'
import { MutableRefObject, useEffect, useRef } from 'react'

const cache = new Set<string>()

const handle = (id: string, operation: () => boolean | null) => {
  try {
    return sendOpenapiHydrationCacheResponse({
      id,
      value: operation(),
      success: true,
    })
  } catch (error) {
    return sendOpenapiHydrationCacheResponse({
      id,
      value: null,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    })
  }
}

export const useHostOpenapiHydrationCache = (
  iframeRef: MutableRefObject<HTMLIFrameElement | null>,
  isActive: boolean,
) => {
  const unsubscribeRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!isActive) {
      unsubscribeRef.current()
      return
    }

    unsubscribeRef.current = listenMessage({
      sourceWindow: iframeRef.current?.contentWindow ?? null,
      onMessage: (action) => {
        if (requestOpenapiHydrationCacheHas.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<
            typeof requestOpenapiHydrationCacheHas
          >

          sendMessage({
            action: handle(id, () => cache.has(key)),
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendOpenapiHydrationCacheResponse],
          })
        }

        if (requestOpenapiHydrationCacheAdd.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<
            typeof requestOpenapiHydrationCacheAdd
          >

          sendMessage({
            action: handle(id, () => {
              cache.add(key)
              return true
            }),
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendOpenapiHydrationCacheResponse],
          })
        }

        if (requestOpenapiHydrationCacheRemove.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<
            typeof requestOpenapiHydrationCacheRemove
          >

          sendMessage({
            action: handle(id, () => cache.delete(key)),
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendOpenapiHydrationCacheResponse],
          })
        }

        if (requestOpenapiHydrationCacheClear.match(action)) {
          const {
            payload: { id },
          } = action as unknown as ReturnType<
            typeof requestOpenapiHydrationCacheClear
          >

          sendMessage({
            action: handle(id, () => {
              cache.clear()
              return true
            }),
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendOpenapiHydrationCacheResponse],
          })
        }
      },
      whitelist: [
        requestOpenapiHydrationCacheHas,
        requestOpenapiHydrationCacheAdd,
        requestOpenapiHydrationCacheRemove,
        requestOpenapiHydrationCacheClear,
      ],
    })

    return () => {
      unsubscribeRef.current()
    }
  }, [isActive])
}
